import { useState } from 'react';
import { Control } from 'react-hook-form';
import {
    Grid,
    Paper,
    TabPanel,
    useTheme,
    EnvTypes,
    useAppSelector,
    RootState,
} from '../../../base';
import { StyledTabs, StyledTab } from './SettingsTabs.styles';
import CompanyInformationTab from '../CompanyInformationTab';
import { useTranslation } from 'react-i18next';
import EmailSettingsTab from '../EmailSettingsTab';
import SocialMediaTab from '../SocialMediaTab';
import NotificationsTab from '../NotificationsTab';
import TeamMembersTab from '../TeamMembersTab';
import RejectReasons from '../RejectReasons';
import { a11yProps, FeatureFlags } from '../../../common';
import { organizationSelectors } from '../../../actions';

interface Props {
    control: Control<any>;
}

const isProduction = process.env.VITE_ENV_TYPE === EnvTypes.PRODUCTION;

const SettingsTabs = ({ control }: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [value, setValue] = useState(0);
    const isEmailSettingsOn = useAppSelector((state: RootState) =>
        organizationSelectors.isFlagOn(state, FeatureFlags.EMAIL_SETTINGS)
    );
    const isDevelopmentOn = useAppSelector((state: RootState) =>
        organizationSelectors.isFlagOn(state, FeatureFlags.DEVELOPMENT)
    );

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    const tabsList = [
        {
            label: t('settings.tabs.companyInformation'),
            component: <CompanyInformationTab control={control} />,
            disabled: false,
        },
        {
            label: t('settings.tabs.socialMedia'),
            component: <SocialMediaTab />,
            disabled: isProduction,
        },
        {
            label: t('settings.tabs.notifications'),
            component: <NotificationsTab control={control} />,
            disabled: isProduction,
        },
        {
            label: t('settings.tabs.companySettings'),
            component: <RejectReasons />,
            disabled: isProduction,
        },
        ...(isDevelopmentOn
            ? [
                  {
                      label: t('settings.tabs.userManagement'),
                      component: <TeamMembersTab />,
                      disabled: false,
                  },
              ]
            : []),
        {
            label: t('settings.tabs.emailSettings'),
            component: <EmailSettingsTab />,
            disabled: !isEmailSettingsOn,
        },
    ];

    return (
        <Paper>
            <Grid container>
                <Grid item xs={12} md={3}>
                    <StyledTabs
                        orientation="vertical"
                        variant="scrollable"
                        value={value}
                        onChange={handleChange}
                        aria-label="Vertical tabs example"
                    >
                        {tabsList.map((tab, index) => (
                            <StyledTab
                                key={index}
                                label={tab.label}
                                disabled={tab.disabled}
                                {...a11yProps(index)}
                            />
                        ))}
                    </StyledTabs>
                </Grid>
                <Grid item xs={12} md={9}>
                    {tabsList.map((tab, index) => (
                        <TabPanel
                            key={index}
                            value={value}
                            index={index}
                            style={{ width: '100%' }}
                            boxStyle={{ padding: theme.spacing(3) }}
                        >
                            {tab.component}
                        </TabPanel>
                    ))}
                </Grid>
            </Grid>
        </Paper>
    );
};

export default SettingsTabs;
